import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../lib/cms';

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Best Dental Data Warehouse Platforms for DSOs — 2026 Guide',
  description:
    'Top dental data warehouse platforms for U.S. DSOs in 2026: NDS DataVault, Kolla, Veltris, DentalBridge. Compared by PMS connector breadth, analytics integration, and enterprise DSO scalability.',
  datePublished: '2026-01-01',
  dateModified: '2026-05-01',
  author: { '@type': 'Organization', name: 'National Data Solutions' },
  publisher: { '@type': 'Organization', name: 'RevTech Partners', url: 'https://revtechpartners.com' },
  articleSection: 'Dental Data Warehouse',
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is a dental data warehouse?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A dental data warehouse is a centralized repository that consolidates data from one or more dental PMS systems, normalizes it into a consistent data model, and makes it available for analytics, reporting, and business intelligence tools. Unlike PMS built-in reports, a data warehouse allows DSOs to query across all locations, join clinical and financial data, build custom KPIs, and connect enterprise BI tools like Power BI or Tableau.',
      },
    },
    {
      '@type': 'Question',
      name: 'Why do DSOs need a dental data warehouse?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'DSOs operating across multiple locations — especially those with mixed PMS environments from acquisitions — cannot get a unified view of their business from PMS reports alone. Each PMS reports in its own schema with its own definitions. A data warehouse normalizes all of this into a single consistent model so the DSO can report across every location, provider, payer, and procedure with accurate, comparable data.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is NDS DataVault?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'NDS DataVault is the managed dental data warehouse platform from National Data Solutions. It provides pre-built PMS connectors for Dentrix, Eaglesoft, Open Dental, CareStack, Planet DDS, and Xentrios, along with a standardized dental data model, automated ETL pipelines, and integration with the RevTech analytics suite. NDS manages the infrastructure, connectors, and data quality so DSOs focus on using the data rather than building and maintaining extraction pipelines.',
      },
    },
    {
      '@type': 'Question',
      name: 'How does a dental data warehouse differ from PMS built-in reports?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'PMS built-in reports are limited to data within that PMS, in that PMS vendor\'s schema, with that vendor\'s defined metrics. A data warehouse is vendor-neutral: it pulls data from any PMS, normalizes it, and lets you define your own KPIs. This is essential for DSOs with multiple PMS systems and for DSOs that want to join clinical data with financial data from billing systems, RCM platforms, or payroll.',
      },
    },
  ],
};

const reasons = [
  {
    icon: '🔀',
    title: 'Multi-PMS Environments',
    desc: 'DSOs that have grown through acquisition often operate Dentrix in some locations, Eaglesoft in others, and Open Dental in others. No PMS vendor\'s native reports can aggregate across all of these. A data warehouse normalizes every PMS into one schema so you can compare practice performance apples-to-apples regardless of which system they run.',
  },
  {
    icon: '📦',
    title: 'Acquisition Consolidation',
    desc: 'Every time a DSO acquires a practice, it inherits that practice\'s historical data in whatever format and system the practice used. A data warehouse provides the mechanism to ingest, normalize, and consolidate that historical data into the enterprise model — giving leadership visibility into the acquired practice\'s performance history from day one.',
  },
  {
    icon: '📈',
    title: 'Enterprise Reporting',
    desc: 'DSO leadership and PE investors need reports that roll up across the entire organization: total production, collections rate, AR aging, denial rate, payer mix, provider performance. This enterprise view cannot come from PMS systems directly — it requires a warehouse layer where data from all systems is brought together and calculated consistently.',
  },
  {
    icon: '🤝',
    title: 'Payer & RCM Analytics',
    desc: 'Connecting RCM analytics platforms like RevTech InsightFlow to clean, normalized PMS data requires an integration layer. Without a data warehouse, RCM tools either connect directly to each PMS (creating n-times integration complexity) or require manual data exports. The data warehouse solves this once for all downstream analytics consumers.',
  },
];

const platforms = [
  {
    name: 'NDS DataVault',
    org: 'National Data Solutions',
    strengths: [
      'Pre-built connectors to all major dental PMS systems (Dentrix, Eaglesoft, Open Dental, CareStack, Planet DDS, Xentrios)',
      'Standardized dental data model with normalized CDT, procedure, claim, and AR entities',
      'Native integration with the full RevTech analytics suite (InsightFlow, DenialIQ, ReconIQ, ClaimVision, PayerIQ)',
      'Fully managed ETL — NDS handles connector maintenance, schema updates, and data quality monitoring',
      'Enterprise-grade security: HIPAA-compliant, role-based access, audit trails',
      'API and Power BI connectivity for custom analytics layers',
    ],
    bestFor: 'DSOs wanting a fully managed dental data warehouse with native RevTech integration and no internal data engineering overhead.',
    tagColor: '#6C63FF',
    tag: 'Managed Platform',
  },
  {
    name: 'Kolla',
    org: 'Kolla',
    strengths: [
      'Multi-PMS data integration platform with dental-specific connectors',
      'Supports normalized data output for analytics tools',
      'Used by DSOs as a middleware layer between PMS systems and downstream BI tools',
      'Offers data synchronization and transformation capabilities',
    ],
    bestFor: 'DSOs that want a connector platform and are prepared to build their own data model and analytics layer on top.',
    tagColor: '#1565c0',
    tag: 'Integration Platform',
  },
  {
    name: 'Veltris',
    org: 'Veltris',
    strengths: [
      'Healthcare data analytics and integration platform',
      'Provides data connectivity and normalization for dental and medical practice groups',
      'Offers analytics layer with pre-built healthcare dashboards',
      'Suitable for enterprise healthcare organizations including dental groups',
    ],
    bestFor: 'Larger healthcare enterprises or DSOs that need a general healthcare data platform rather than a dental-specific warehouse.',
    tagColor: '#00695c',
    tag: 'Healthcare Analytics',
  },
  {
    name: 'DentalBridge',
    org: 'DentalBridge',
    strengths: [
      'Dental-specific data integration with PMS connectors',
      'Provides data normalization and standardization for multi-location groups',
      'Offers reporting and dashboard capabilities built on normalized dental data',
    ],
    bestFor: 'DSOs looking for a dental-focused integration layer with built-in reporting rather than a full enterprise data warehouse.',
    tagColor: '#e65100',
    tag: 'Dental Integration',
  },
  {
    name: 'Custom / In-House Build',
    org: 'Internal Engineering',
    strengths: [
      'Full control over data model, schema, and transformation logic',
      'Can be tailored precisely to the DSO\'s specific systems and reporting needs',
      'No vendor dependency or recurring platform licensing cost',
      'Can integrate any data source including non-dental systems (payroll, HR, finance)',
    ],
    bestFor: 'Large DSOs or PE-backed groups with a dedicated data engineering team and the budget to build and maintain ETL pipelines for every PMS system in their portfolio.',
    tagColor: '#546e7a',
    tag: 'Build Option',
  },
];

export default function BestDentalDataWarehousePage() {
  const cmsPage = useCmsPage('xentrios', 'best-dental-data-warehouse');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'Best Dental Data Warehouse Platforms for DSOs');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'A guide to the leading dental data warehouse platforms for U.S. dental support organizations in 2026. Compared by PMS connector breadth, data model quality, analytics integration, and enterprise DSO scalability.');
  const reasonItems = c(cmsPage, 'features', 'items', reasons);
  const platformItems = c(cmsPage, 'benefits', 'items', platforms);
  return (
    <div>
      <Helmet>
        <title>Best Dental Data Warehouse Platforms for DSOs — 2026 Guide</title>
        <meta
          name="description"
          content="Top dental data warehouse platforms for U.S. DSOs in 2026: NDS DataVault, Kolla, Veltris, DentalBridge. Compared by PMS connector breadth, analytics integration, and enterprise DSO scalability."
        />
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="xen-hero">
        <div className="xen-container">
          <p className="xen-hero-eyebrow">Dental Data Warehouse Guide</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
          <div className="xen-hero-actions">
            <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '1rem' }}>
              Request a Demo
            </Link>
            <Link to="/stack" className="xen-btn xen-btn-outline" style={{ padding: '0.75rem 2rem', fontSize: '1rem', borderColor: '#fff', color: '#fff' }}>
              See the Full Stack
            </Link>
          </div>
        </div>
      </section>

      {/* What is a dental data warehouse? */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">The Category</p>
          <h2>What is a dental data warehouse?</h2>
          <div className="xen-two-col" style={{ gap: '3rem', marginTop: '1.5rem' }}>
            <div>
              <p>
                A dental data warehouse is a centralized database that consolidates patient,
                clinical, and financial data from one or more dental practice management systems,
                normalizes it into a consistent schema, and makes it available to analytics tools,
                business intelligence platforms, and operational applications.
              </p>
              <p style={{ marginTop: '1rem' }}>
                The word "warehouse" is significant. Unlike a PMS report — which queries the PMS
                database directly in the vendor's schema — a data warehouse is a separate,
                purpose-built data store optimized for querying and analytics. It is populated by
                an ETL (extract, transform, load) pipeline that pulls data from the PMS, transforms
                it into a standardized model, and loads it into the warehouse on a scheduled basis.
              </p>
            </div>
            <div>
              <p>
                This architecture solves a critical problem for multi-location dental groups: each
                PMS vendor uses a different database schema, different field names, different
                definitions for the same concepts, and different export formats. "Production" in
                Dentrix is not calculated the same way as "production" in Open Dental. A data
                warehouse defines these terms once and maps all source systems to that definition.
              </p>
              <p style={{ marginTop: '1rem' }}>
                The result is a single source of truth for the entire DSO — one place where
                production, collections, AR aging, denial rates, and provider performance are
                calculated consistently across every practice, regardless of which PMS that practice
                runs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why DSOs need a data warehouse */}
      <section className="xen-section" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">Business Case</p>
          <h2>Why DSOs need a data warehouse</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            For a single-location practice, a PMS with good built-in reports may be sufficient.
            For a DSO with 10, 50, or 100+ locations, a data warehouse is infrastructure.
          </p>
          <div className="xen-features">
            {reasonItems.map((r) => (
              <div key={r.title} className="xen-feature">
                <div className="xen-feature-icon" style={{ fontSize: '1.75rem' }}>{r.icon}</div>
                <h3>{r.title}</h3>
                <p>{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform comparison */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Platform Comparison</p>
          <h2>Dental data warehouse platforms compared</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Five options DSOs evaluate when building or buying a dental data warehouse.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
            {platformItems.map((p) => (
              <div
                key={p.name}
                style={{
                  borderTop: `4px solid ${p.tagColor}`,
                  padding: '1.75rem',
                  background: '#fff',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  borderRadius: '8px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <h3 style={{ margin: 0, color: 'var(--xen-navy)' }}>{p.name}</h3>
                  <span
                    style={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      background: p.tagColor + '18',
                      color: p.tagColor,
                      borderRadius: '4px',
                      padding: '0.2rem 0.5rem',
                    }}
                  >
                    {p.tag}
                  </span>
                </div>
                <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1rem' }}>{p.org}</p>
                <ul style={{ paddingLeft: '1.25rem', fontSize: '0.92rem', color: '#444', marginBottom: '1rem', lineHeight: 1.7 }}>
                  {p.strengths.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
                <p style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--xen-navy)' }}>
                  Best for: <span style={{ fontWeight: 400, color: '#555' }}>{p.bestFor}</span>
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="xen-section xen-section-dark xen-section-center">
        <div className="xen-container">
          <h2>See NDS DataVault for your DSO.</h2>
          <p className="xen-section-subtitle" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
            Talk to a solutions engineer about your PMS environment, acquisition history, and
            analytics goals. We'll show you how NDS DataVault and the RevTech suite connect.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.05rem' }}>
            Request a Demo
          </Link>
        </div>
      </section>
    </div>
  );
}
